import React, { FunctionComponent, useEffect, useState } from 'react';
import { Modal, Form, Row, Button, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useStores } from 'Hooks';
import { Amenity as AmenityIcon, Close } from 'Components/icons';
import { BookingPreviewDto } from 'Api/Features/Bookings/Dtos/BookingPreviewDto';
import './booking-conflicts-modal.less';
import { BookingConflictItem } from 'Components/booking/booking-conflict/booking-conflict-item';
import moment from 'moment';
import { Amenity } from 'Models/Amenities/Amenity';
import { CreateBookingRecurrenceExceptionDto } from 'Api/Features/Bookings/Dtos/CreateBookingRecurrenceExceptionDto';
import EditBookingConflictModal from './edit-booking-conflict-modal';
import { DailyBookingSummaryPeriodUnavailabilityReasonDto } from 'Api/Features/Bookings/Dtos/DailyBookingSummaryPeriodUnavailabilityReasonDto';

interface BookingConflictModalProps {
    visible: boolean;
    onComplete: (
        success: boolean,
        exceptions: CreateBookingRecurrenceExceptionDto[] | null
    ) => void;
    amenity: Amenity;
    bookingsWithRecurrenceErrors: BookingPreviewDto[];
    timezone: string | null;
    locationId?: string;
    resourcePeriod: any;
}

export interface CurrentConflict {
    index: number;
    bookingStart: string;
    bookingEnd: string;
    conferenceRoomId: string;
    resolved?: boolean;
}

export interface ConflictBookingPreview extends BookingPreviewDto {
    resolved?: boolean;
}

const BookingConflictModal: FunctionComponent<BookingConflictModalProps> = ({
    visible,
    onComplete,
    amenity,
    bookingsWithRecurrenceErrors,
    timezone,
    locationId,
    resourcePeriod
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { confirmationModalStore } = useStores();
    const [bookings, setBookings] = useState<ConflictBookingPreview[]>([]);
    const [editConflictModalOpen, setEditConflictModalOpen] = useState(false);
    const [currentEditingConflict, setCurrentEditingConflict] = useState<CurrentConflict>();

    useEffect(() => {
        setBookings(bookingsWithRecurrenceErrors);
    }, [bookingsWithRecurrenceErrors]);

    const onEditConflictClick = (index: number): void => {
        const conflict = bookings.find(booking => booking.index === index);
        if(conflict) {
            setCurrentEditingConflict({
                bookingStart: conflict.periodStart!,
                bookingEnd: conflict.periodEnd!,
                conferenceRoomId: amenity.id,
                index: index,
                resolved: conflict.resolved
            })
            setEditConflictModalOpen(true);
        }
    }

    const onEditConflictComplete = (resolvedConflict?: CurrentConflict): void => {
        setEditConflictModalOpen(false);
        if(resolvedConflict) {
            const oldState = bookings;
            const index = oldState.findIndex((booking) => booking.index === resolvedConflict.index);
            oldState[index] = {
                index: resolvedConflict.index,
                isAvailable: false, 
                isCancelled: false,
                periodEnd: resolvedConflict.bookingEnd,
                periodStart: resolvedConflict.bookingStart,
                unavailabilityReason: DailyBookingSummaryPeriodUnavailabilityReasonDto.Booked,
                resolved: true
            } as ConflictBookingPreview ;
            setBookings([...oldState]);
        }
    }
    
    const dismiss = (
        success = false,
        exceptions: CreateBookingRecurrenceExceptionDto[] | null
    ): void => {
        onComplete(success, exceptions);
    };

    const exit = async (): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <AmenityIcon />,
                title: t(`Booking.book_a_room_confirm_title`),
                message: t(`Booking.book_a_room_confirm_message`),
                positiveText: t('Booking.leave_booking_conflicts_confirm_positive'),
                negativeText: t(`Booking.book_a_room_confirm_negative`),
            }))
        )
            return;
        dismiss(false, null);
    };

    const cancelBooking = async (index: number): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <AmenityIcon />,
                title: t(`Booking.delete_conflict_confirm_title`),
                message: t(`are_you_sure_you_want_to_continue`),
                positiveText: t('yes'),
                negativeText: t('no'),
            }))
        )
            return;

        const oldState = bookings;
        oldState.find((booking) => booking.index === index)!.isCancelled = true;
        setBookings([...oldState]);
    };
    
    const createExceptions = (): CreateBookingRecurrenceExceptionDto[] => {
        const exceptions = bookings.map(
            (booking) =>
                ({
                    index: booking.index,
                    periodStart: booking.periodStart,
                    periodEnd: booking.periodEnd,
                    isCancelled: booking.isCancelled || !booking.resolved,
                } as CreateBookingRecurrenceExceptionDto)
        );

        return exceptions;
    };

    const submit = (ignoreConflicts?: boolean): void => {
        if(!ignoreConflicts && bookings.some(booking => !booking.isCancelled && !booking.resolved)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            cancelUnresolvedConflicts();
            return;
        }
        const exceptions = createExceptions();
        dismiss(true, exceptions);
    };

    const cancelUnresolvedConflicts = async (): Promise<void> => {
        if (
            !(await confirmationModalStore.confirm({
                icon: <AmenityIcon />,
                title: t(`Booking.cancel_booking_conflicts`), 
                message: t(`Booking.cancel_booking_conflicts_explanation`),
                positiveText: t('yes'),
                negativeText: t(`no`),
            }))
        )
            return;
        submit(true);
    };

    return (
        <>
            <Modal
                visible={visible}
                title={t('Booking.booking_conflicts')}
                className="FormModal BookingConflictModal"
                closeIcon={<Close />}
                width={960}
                footer={null}
                onCancel={(): Promise<void> => exit()}
                maskClosable={false}
                centered
            >
                <Form layout="vertical" form={form}>
                    {bookings.some((booking) => !booking.isCancelled) ? (
                        <>
                            <Row>
                                <Col span={24} className="title-container">
                                    {t('Booking.room_and_period_not_available')}
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    {bookings.map((booking, i, arr) => (
                                        <div
                                            className={
                                                booking.isCancelled || i === arr.length - 1
                                                    ? ''
                                                    : 'pb-15'
                                            }
                                            key={booking.index}
                                        >
                                            <BookingConflictItem
                                                index={booking.index!}
                                                resolved={booking.resolved}
                                                periodStart={moment(booking.periodStart).tz(timezone ?? '')}
                                                periodEnd={moment(booking.periodEnd).tz(timezone ?? '')}
                                                amenity={amenity}
                                                errorMessage={t(
                                                    `Booking.unavailability_reason_${booking.unavailabilityReason}`
                                                )}
                                                onDeleteClick={(index: number): Promise<void> =>
                                                    cancelBooking(index)
                                                }
                                                onEditClick={(index: number): void =>
                                                    onEditConflictClick(index)
                                                }
                                                isCanceled={booking.isCancelled}
                                            />
                                        </div>
                                    ))}
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <Row>
                            <Col span={24} className="all-deleted-container">
                                {t('Booking.all_conflicts_deleted')}
                            </Col>
                        </Row>
                    )}

                    <div className="actions d-flex">
                        <Button
                            type="default"
                            className="secondary negative m-l-auto"
                            htmlType="button"
                            onClick={(): Promise<void> => exit()}
                        >
                            {t('cancel')}
                        </Button>
                        <Button type="primary" className="positive" onClick={(): void => submit()}>
                            {t('Booking.confirm_my_recurring_booking')}
                        </Button>
                    </div>
                </Form>
            </Modal>

            {editConflictModalOpen && (
                <EditBookingConflictModal
                    onComplete={(resolvedConflict?: CurrentConflict): void =>
                        onEditConflictComplete(resolvedConflict)
                    }
                    visible={editConflictModalOpen}
                    timezone={timezone}
                    resourcePeriod={resourcePeriod}
                    conflict={currentEditingConflict}
                    amenity={amenity}
                    locationId={locationId}
                />
            )}
        </>
    );
};

export default React.memo(BookingConflictModal);
